// Rule: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
import { Controller } from "@hotwired/stimulus"

/**
 * Campsite Type Selection Controller
 * 
 * Manages the campsite type selection UI and shows/hides the RV length input
 * when RV camping is selected.
 */
export default class extends Controller {
  static targets = ["option", "input", "rvSection"]

  connect() {
    // Initialize the controller
    this.updateSelection()
  }

  /**
   * Handle the selection of a campsite type
   * @param {Event} event - The click event
   */
  select(event) {
    const selectedOption = event.currentTarget
    const typeId = selectedOption.dataset.typeId

    // Update the hidden input value
    if (this.hasInputTarget) {
      this.inputTarget.value = typeId
    }

    // Update the UI to reflect the selection
    this.updateSelectionUI(selectedOption)

    // Show/hide RV section if needed
    this.toggleRvSection(typeId)
  }

  /**
   * Update the UI to reflect the current selection
   * @param {HTMLElement} selectedOption - The selected option element
   */
  updateSelectionUI(selectedOption) {
    // Remove selected state from all options
    this.optionTargets.forEach(option => {
      option.classList.remove("border-orange-500", "bg-orange-50", "text-orange-700")
      option.classList.add("border-gray-200", "bg-white", "text-gray-700", "hover:border-orange-300")
      
      // Remove the selection indicator
      const indicator = option.querySelector(".absolute.top-2.right-2")
      if (indicator) indicator.remove()
      
      // Update icon color
      const icon = option.querySelector("i")
      if (icon) icon.classList.remove("text-orange-600")
      if (icon) icon.classList.add("text-gray-500")
      
      // Remove selected badge if it exists
      const badge = option.querySelector(".rounded-full")
      if (badge) badge.remove()
      
      // Update aria-selected attribute
      option.setAttribute("aria-selected", "false")
      option.dataset.selected = "false"
    })

    // Add selected state to the clicked option
    selectedOption.classList.remove("border-gray-200", "bg-white", "text-gray-700")
    selectedOption.classList.add("border-orange-500", "bg-orange-50", "text-orange-700")
    
    // Add selection indicator
    if (!selectedOption.querySelector(".absolute.top-2.right-2")) {
      const indicator = document.createElement("div")
      indicator.className = "absolute top-2 right-2 h-3 w-3 rounded-full bg-orange-500"
      selectedOption.appendChild(indicator)
    }
    
    // Update icon color
    const icon = selectedOption.querySelector("i")
    if (icon) icon.classList.remove("text-gray-500")
    if (icon) icon.classList.add("text-orange-600")
    
    // Add selected badge if it doesn't exist
    if (!selectedOption.querySelector(".rounded-full.bg-orange-100")) {
      const iconContainer = selectedOption.querySelector(".text-center")
      if (iconContainer) {
        const badge = document.createElement("span")
        badge.className = "mt-2 inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700"
        
        const dot = document.createElement("svg")
        dot.className = "-ml-0.5 mr-1.5 h-2 w-2 text-orange-500"
        dot.setAttribute("fill", "currentColor")
        dot.setAttribute("viewBox", "0 0 8 8")
        
        const circle = document.createElement("circle")
        circle.setAttribute("cx", "4")
        circle.setAttribute("cy", "4")
        circle.setAttribute("r", "3")
        
        dot.appendChild(circle)
        badge.appendChild(dot)
        badge.appendChild(document.createTextNode("Selected"))
        
        iconContainer.appendChild(badge)
      }
    }
    
    // Update aria-selected attribute
    selectedOption.setAttribute("aria-selected", "true")
    selectedOption.dataset.selected = "true"
  }

  /**
   * Update the selection based on the current input value
   */
  updateSelection() {
    if (!this.hasInputTarget) return
    
    const currentValue = this.inputTarget.value
    const selectedOption = this.optionTargets.find(
      option => option.dataset.typeId === currentValue
    )
    
    if (selectedOption) {
      this.updateSelectionUI(selectedOption)
      this.toggleRvSection(currentValue)
    }
  }

  /**
   * Toggle the RV section visibility based on the selected type
   * @param {string} typeId - The selected campsite type ID
   */
  toggleRvSection(typeId) {
    if (!this.hasRvSectionTarget) return
    
    if (typeId === 'rv_camping') {
      this.rvSectionTarget.classList.remove('hidden')
    } else {
      this.rvSectionTarget.classList.add('hidden')
    }
  }
}
